// © Microsoft Corporation. All rights reserved.

import { ExperienceDeferralEventTypes, ExperienceDeferralInfo } from "../data-model";

import { ExperienceLoadingState } from "../data-model/ExperienceLoadingState";
import { IActionPayload } from "@msnews/experiences-redux";
import { MilestoneDataActions } from "../MilestoneData.actions";
import { getMilestoneDataConnector } from "../MilestoneData.Helper";

/**
 * Identifies a referenced experience and its dependents
 */
export interface ExperienceDependents {
    /**
     * Determines the state of deferral identifier experience.
     */
    currentState: ExperienceLoadingState;

    /**
     * The dependent set of experiences and its associated state mapped to a deferred event type.
     * An experience moves from deferred to resolvedForLoad or resolvedForRender state when deferral identifier experienceType/Group currentState matches deferred state.
     */
    referencedDependentsDeferralMap: Map<ExperienceDeferralEventTypes, Map<string, ExperienceLoadingState>>
}

// Keeps track of Experience Identifier to its referenced dependents mapping for loadAfter/renderAfter scenario
// Experience Identifier, acts as a reference to resolve its dependents.
// The key (experienceIdentifier) should be either experienceType or experienceGroup
let experienceDeferralDependencyMap: Map<string, ExperienceDependents> = new Map<string, ExperienceDependents>();

/**
 * Resolves experience dependency
 * @param experienceType - experience loaded/rendered
 * @param experienceDependencyMap - experience dependency map to be updated
 */
function resolveExperienceDependency(): void {

    MilestoneDataActions.updateExperienceCurrentState.registerObserver((actionPayload: IActionPayload) => {
        // Make sure this is a rendered state change
        if (!actionPayload.params ||
            !actionPayload.params[0] ||
            !actionPayload.params[1]) {
            return;
        }

        const experienceType = actionPayload.params[0];
        const experienceCurrentState = actionPayload.params[1];

        const expDependencies = experienceDeferralDependencyMap.get(experienceType);

        if (!expDependencies) {
            return;
        }

        expDependencies.currentState = experienceCurrentState;
        const deferralEventDependents = expDependencies.referencedDependentsDeferralMap.get(experienceCurrentState);

        if (!deferralEventDependents) {
            return;
        }

        deferralEventDependents.forEach((deferredState, dependentExp) => {
            if (!dependentExp) {
                return;
            }

            const resolvedState = deferredState === ExperienceLoadingState.loadDeferred ?
                                    ExperienceLoadingState.resolvedForLoad
                                    : ExperienceLoadingState.resolvedForRender;

            const milestoneDataConnector = getMilestoneDataConnector();
            if (milestoneDataConnector) {
                MilestoneDataActions.updateExperienceCurrentState.getActionSender(milestoneDataConnector)
                .send(dependentExp, resolvedState);
            }
        });

    });
}

/**
 * Update experience dependency map with new experience deferred information
 * @param actionPayload - actionPayload contains experience deferral information
 * @param experienceDependencyMap - experience dependency map to be updated
 */
function updateExperienceDependencyMap(experienceType: string, expDeferralInfo: ExperienceDeferralInfo, deferredState: ExperienceLoadingState): void {

    if (!experienceType
        || !expDeferralInfo
        || !(expDeferralInfo.eventType in ExperienceDeferralEventTypes)
        || !(expDeferralInfo.experienceType || expDeferralInfo.experienceGroup)) {
        return;
    }

    const deferralEventType = expDeferralInfo.eventType;
    const expDeferralIdentifier = expDeferralInfo.experienceType || expDeferralInfo.experienceGroup;
    const existingDependentsInfo = experienceDeferralDependencyMap.get(expDeferralIdentifier);
    const dependents = new Map<string, ExperienceLoadingState>();

    if (!existingDependentsInfo || !existingDependentsInfo.referencedDependentsDeferralMap) {
        const referencedDependentsDeferralMap = new Map<ExperienceDeferralEventTypes, Map<string, ExperienceLoadingState>>();
        dependents.set(experienceType, deferredState);
        referencedDependentsDeferralMap.set(deferralEventType, dependents);
        experienceDeferralDependencyMap.set(expDeferralIdentifier,
                                            { currentState: ExperienceLoadingState.notStarted,
                                                referencedDependentsDeferralMap: referencedDependentsDeferralMap });
        return;
    } else {

        if (existingDependentsInfo.currentState >= expDeferralInfo.eventType) {
            // if deferral identifier eventType is already met, return and don't add dependencies
            return;
        }

        if (!existingDependentsInfo.referencedDependentsDeferralMap.has(deferralEventType)) {
            dependents.set(experienceType, deferredState);
            existingDependentsInfo.referencedDependentsDeferralMap.set(deferralEventType, dependents);
        } else {
            const existingDependents = existingDependentsInfo.referencedDependentsDeferralMap.get(expDeferralInfo.eventType);
            if (existingDependents.size > 0) {
                existingDependents.set(experienceType, deferredState);
            }
        }
    }
}

/**
 * register observer to update experience load dependency map
 */
function registerExperienceLoadDeferralObserver(): void {
    MilestoneDataActions.updateExperienceLoadDeferralState.registerObserver((actionPayload: IActionPayload) => {
        if (!actionPayload.params &&
            !actionPayload.params[0] &&
            actionPayload.params[0].eventType !== ExperienceDeferralEventTypes.loaded &&
            !actionPayload.params[1]) {
            return;
        }

        updateExperienceDependencyMap(actionPayload.params[1], actionPayload.params[0], ExperienceLoadingState.loadDeferred);
    });
}

/**
 * Register observer to update experience render dependency map
 */
function registerExperienceRenderDeferralObserver(): void {
    MilestoneDataActions.updateExperienceRenderDeferralState.registerObserver((actionPayload: IActionPayload) => {
        if (!actionPayload.params &&
            !actionPayload.params[0] &&
            actionPayload.params[0].eventType !== ExperienceDeferralEventTypes.rendered &&
            !actionPayload.params[1]) {
            return;
        }

        updateExperienceDependencyMap(actionPayload.params[1], actionPayload.params[0], ExperienceLoadingState.renderDeferred);
    });
}

/**
 * registers observers to add and resolve experience dependencies
 */
export function startExperinceDeferralDependencyObserver(): void {
    registerExperienceLoadDeferralObserver();
    registerExperienceRenderDeferralObserver();
    resolveExperienceDependency();
}

/**
 * Checks if experience's dependency is resolved
 * @returns true if dependent experience has been resolved.
 * @param experienceDependency - dependent experience
 * @param deferralEventType - deferral EventType
 */
export function isExperienceDependencyResolved(experienceDependency: string, deferralEventType: ExperienceDeferralEventTypes): boolean {

    const deferralIdentifier = experienceDeferralDependencyMap.get(experienceDependency);

    if (!deferralIdentifier) {
        return true;
    }

    return deferralIdentifier.currentState >= deferralEventType;
}